import { useState, useEffect, useRef } from 'react';
import {
    hexToHSL,
    hslToHex,
    validateHex
} from "./ColorUtils";

import { getContrastRatio } from "./getTextColor";

const CustomColorPicker = ({ color, validHex = true, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const initializeColorState = (initialColor) => {
        const validInitialColor = validateHex(initialColor) ? initialColor : '#ff0000';
        const { h, s, l } = hexToHSL(validInitialColor);

        // Convert HSL to HSV
        const v = l + s * Math.min(l, 1 - l);
        const newSat = v === 0 ? 0 : 2 * (1 - l / v);
        const hueValue = Math.round(h * 360);

        return {
            color: validInitialColor,
            h: hueValue,
            s: newSat,
            b: v
        };
    };

    const initialState = initializeColorState(color);
    const [currentColor, setCurrentColor] = useState(initialState.color);
    const [hue, setHue] = useState(initialState.h);
    const [saturation, setSaturation] = useState(initialState.s);
    const [brightness, setBrightness] = useState(initialState.b);

    // prevent max depth exceeded error
    const isThrottled = useRef(false);


    // Track whether we're animating (to prevent unnecessary redraws)
    const isAnimating = useRef(false);

    // Refs for canvas elements
    const pickerRef = useRef(null);
    const hueCanvasRef = useRef(null);
    const satCanvasRef = useRef(null);

    // Refs for 2D contexts
    const hueContextRef = useRef(null);
    const satContextRef = useRef(null);

    // Track current hue value for immediate access
    const currentHueRef = useRef(0);

    // Track whether we're dragging and what we're dragging
    const dragTypeRef = useRef(null);

    // For tracking meaningful hues with grayscale colors
    const lastMeaningfulHue = useRef(0);

    // Track if update is internal
    const isInternalUpdate = useRef(false);

    const SAT_CANVAS_WIDTH = 200;
    const SAT_CANVAS_HEIGHT = 200;
    const HUE_CANVAS_WIDTH = 200;
    const HUE_CANVAS_HEIGHT = 30;

    // Store cursor positions for rendering
    const cursorPositionRef = useRef({
        hue: 0,        // X position in the hue canvas (0-256)
        satX: 0,       // X position in the saturation canvas (0-256)
        satY: 0        // Y position in the saturation canvas (0-256)
    });

    // Initialize canvases after mounting
    useEffect(() => {
        if (isOpen) {
            initializeCanvases();
        }
    }, [isOpen]);

    // Initialize color when component mounts or color prop changes
    useEffect(() => {
        if (isThrottled.current) return;

        if (color && !isInternalUpdate.current) {

            const validColor = validateHex(color) ? color : currentColor;
            if (validColor === currentColor) return;

            // Set throttle state
            isThrottled.current = true;
            setCurrentColor(validColor);


            const { h, s, l } = hexToHSL(validColor);

            // Convert HSL to HSV
            const v = l + s * Math.min(l, 1 - l);
            const newSaturation = v === 0 ? 0 : 2 * (1 - l / v);

            // For grayscale colors, preserve the last meaningful hue
            let hueValue;
            if (s < 0.01) {
                hueValue = lastMeaningfulHue.current;
            } else {
                hueValue = Math.round(h * 360);
                lastMeaningfulHue.current = hueValue;
            }

            // Update ref for immediate access
            currentHueRef.current = hueValue;

            // Update state
            setHue(hueValue);
            setSaturation(newSaturation);
            setBrightness(v);

            // Update cursor positions
            cursorPositionRef.current = {
                hue: (hueValue / 360) * HUE_CANVAS_WIDTH,
                satX: newSaturation * SAT_CANVAS_WIDTH,
                satY: (1 - v) * SAT_CANVAS_HEIGHT
            };

            // Redraw if canvases are initialized
            if (isOpen && !isAnimating.current) {
                requestAnimationFrame(() => {
                    renderHueCanvas();
                    renderSaturationCanvas(hueValue);
                });
            }
        }

        setTimeout(() => {
            isThrottled.current = false;
        }, 50);

        isInternalUpdate.current = false;
    }, [color, isOpen]);



    // Update after state changes
    useEffect(() => {
        if (isThrottled.current) return;
        // Update ref for current hue value
        currentHueRef.current = hue;

        // Skip during animation frames (those will be handled by the animation loop)
        if (isAnimating.current) return;

        // Update cursor positions based on state values
        cursorPositionRef.current = {
            hue: (hue / 360) * HUE_CANVAS_WIDTH,
            satX: saturation * SAT_CANVAS_WIDTH,
            satY: (1 - brightness) * SAT_CANVAS_HEIGHT
        };

        // Only redraw if the picker is open
        if (isOpen) {
            requestAnimationFrame(() => {
                renderHueCanvas();
                renderSaturationCanvas(hue);
            });
        }
    }, [hue, saturation, brightness, isOpen]);

    // Initialize canvas contexts and draw initial gradients
    const initializeCanvases = () => {
        if (hueCanvasRef.current) {
            const hueCanvas = hueCanvasRef.current;
            hueCanvas.width = HUE_CANVAS_WIDTH;
            hueCanvas.height = HUE_CANVAS_HEIGHT;
            hueContextRef.current = hueCanvas.getContext('2d');
        }

        if (satCanvasRef.current) {
            const satCanvas = satCanvasRef.current;
            satCanvas.width = SAT_CANVAS_WIDTH;
            satCanvas.height = SAT_CANVAS_HEIGHT;
            satContextRef.current = satCanvas.getContext('2d');
        }

        // Initial render
        requestAnimationFrame(() => {
            renderHueCanvas();
            renderSaturationCanvas(currentHueRef.current);
        });
    };

    // Render the hue slider canvas
    const renderHueCanvas = () => {
        const ctx = hueContextRef.current;
        if (!ctx) return;

        // Clear canvas
        ctx.clearRect(0, 0, HUE_CANVAS_WIDTH, HUE_CANVAS_HEIGHT);

        // Draw hue gradient
        const gradient = ctx.createLinearGradient(0, 0, HUE_CANVAS_WIDTH, 0);
        gradient.addColorStop(0, '#ff0000'); // Red
        gradient.addColorStop(1 / 6, '#ffff00'); // Yellow
        gradient.addColorStop(2 / 6, '#00ff00'); // Green
        gradient.addColorStop(3 / 6, '#00ffff'); // Cyan
        gradient.addColorStop(4 / 6, '#0000ff'); // Blue
        gradient.addColorStop(5 / 6, '#ff00ff'); // Magenta
        gradient.addColorStop(1, '#ff0000'); // Red again

        // Fill with gradient
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, HUE_CANVAS_WIDTH, HUE_CANVAS_HEIGHT);

        // Draw hue indicator at cursor position
        const xPos = cursorPositionRef.current.hue;

        // Outer white circle
        ctx.beginPath();
        ctx.arc(xPos, HUE_CANVAS_HEIGHT / 2, 7, 0, 2 * Math.PI);
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 3;
        ctx.stroke();

        // Inner black circle
        ctx.beginPath();
        ctx.arc(xPos, HUE_CANVAS_HEIGHT / 2, 7, 0, 2 * Math.PI);
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 1;
        ctx.stroke();
    };

    // Render the saturation/brightness canvas
    const renderSaturationCanvas = (hueValue) => {
        const ctx = satContextRef.current;
        if (!ctx) return;

        // Use the provided hue value, or fall back to the current state if not provided
        const useHue = hueValue !== undefined ? hueValue : currentHueRef.current;

        // Clear canvas
        ctx.clearRect(0, 0, SAT_CANVAS_WIDTH, SAT_CANVAS_HEIGHT);

        // Fill with white for base
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, SAT_CANVAS_WIDTH, SAT_CANVAS_HEIGHT);

        // Get pure hue color
        const hueColor = `hsl(${useHue}, 100%, 50%)`;

        // Draw saturation gradient (horizontal: white to color)
        const satGradient = ctx.createLinearGradient(0, 0, SAT_CANVAS_WIDTH, 0);
        satGradient.addColorStop(0, 'white');
        satGradient.addColorStop(1, hueColor);

        ctx.fillStyle = satGradient;
        ctx.fillRect(0, 0, SAT_CANVAS_WIDTH, SAT_CANVAS_HEIGHT);

        // Draw brightness gradient (vertical: transparent to black)
        const brightGradient = ctx.createLinearGradient(0, 0, 0, SAT_CANVAS_HEIGHT);
        brightGradient.addColorStop(0, 'rgba(0,0,0,0)');
        brightGradient.addColorStop(1, 'rgba(0,0,0,1)');

        ctx.fillStyle = brightGradient;
        ctx.fillRect(0, 0, SAT_CANVAS_WIDTH, SAT_CANVAS_HEIGHT);

        // Draw saturation/brightness indicator at cursor position
        const xPos = cursorPositionRef.current.satX;
        const yPos = cursorPositionRef.current.satY;

        // Outer white circle
        ctx.beginPath();
        ctx.arc(xPos, yPos, 7, 0, 2 * Math.PI);
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 3;
        ctx.stroke();

        // Inner black circle
        ctx.beginPath();
        ctx.arc(xPos, yPos, 7, 0, 2 * Math.PI);
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 1;
        ctx.stroke();
    };

    // Update color from HSV values
    const updateColorFromHSV = (h, s, v) => {
      if (isThrottled.current) return;

      // Set throttle state
      isThrottled.current = true;

      // Ensure values are within valid ranges
      const newHue = Math.max(0, Math.min(360, h));
      const newSat = Math.max(0, Math.min(1, s));
      const newVal = Math.max(0, Math.min(1, v));

      // Update ref immediately for direct access
      currentHueRef.current = newHue;

      // If saturation is meaningful, save the hue
      if (newSat > 0.01) {
          lastMeaningfulHue.current = newHue;
      }

      // Convert HSV to HSL
      const l = newVal * (1 - newSat / 2);
      const s2 = (l === 0 || l === 1) ? 0 : (newVal - l) / Math.min(l, 1 - l);

      // Generate hex color
      const newColor = hslToHex(newHue / 360, s2, l);

      // Mark as internal update to prevent loops
      isInternalUpdate.current = true;

      // Update state
      setHue(newHue);
      setSaturation(newSat);
      setBrightness(newVal);
      setCurrentColor(newColor);

      // Call onChange callback
      onChange(newColor);

      setTimeout(() => {
        isThrottled.current = false;
      }, 16);
    };

    // Start dragging the hue slider
    const startHueDrag = (e) => {
        e.preventDefault();

        // Set animating flag to prevent unnecessary redraws from state updates
        isAnimating.current = true;

        // Set drag type
        dragTypeRef.current = 'hue';

        // Update cursor position immediately
        updateHuePosition(e);

        // Add event listeners
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', endDrag);
        window.addEventListener('touchmove', handleTouchMove, { passive: false });
        window.addEventListener('touchend', endDrag);
    };

    // Start dragging the saturation area
    const startSaturationDrag = (e) => {
        e.preventDefault();

        // Set animating flag to prevent unnecessary redraws from state updates
        isAnimating.current = true;

        // Set drag type
        dragTypeRef.current = 'saturation';

        // Update cursor position immediately
        updateSaturationPosition(e);

        // Add event listeners
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', endDrag);
        window.addEventListener('touchmove', handleTouchMove, { passive: false });
        window.addEventListener('touchend', endDrag);
    };

    // Update hue position from mouse/touch event
    const updateHuePosition = (e) => {
        if (!hueCanvasRef.current) return;

        // Get canvas-relative coordinates
        const rect = hueCanvasRef.current.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;

        // Calculate x position, clamped to canvas bounds
        const x = Math.max(0, Math.min(rect.width, clientX - rect.left));

        // Update cursor position
        cursorPositionRef.current.hue = x;

        // Calculate new hue (0-360)
        const newHue = Math.round((x / rect.width) * 360);

        // Update ref immediately for direct access
        currentHueRef.current = newHue;

        // Render hue canvas first
        renderHueCanvas();

        // Render saturation canvas with the new hue value directly
        renderSaturationCanvas(newHue);

        // Update color state
        updateColorFromHSV(newHue, saturation, brightness);
    };

    // Update saturation position from mouse/touch event
    const updateSaturationPosition = (e) => {
        if (!satCanvasRef.current) return;

        // Get canvas-relative coordinates
        const rect = satCanvasRef.current.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;

        // Calculate position, clamped to canvas bounds
        const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
        const y = Math.max(0, Math.min(rect.height, clientY - rect.top));

        // Update cursor position
        cursorPositionRef.current.satX = x;
        cursorPositionRef.current.satY = y;

        // Calculate new saturation and brightness (0-1)
        const newSaturation = x / rect.width;
        const newBrightness = 1 - (y / rect.height);

        // Update color
        updateColorFromHSV(currentHueRef.current, newSaturation, newBrightness);

        // Render immediately
        renderSaturationCanvas();
    };

  // Modify handleMouseMove to capture the last position even if throttled
  const handleMouseMove = (e) => {
    e.preventDefault();

    if (!dragTypeRef.current) return;

    // Always update the cursor position visually, even if throttled
    if (dragTypeRef.current === 'hue') {
      // Update cursor position even if color update is throttled
      const rect = hueCanvasRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
      cursorPositionRef.current.hue = x;

      // Still attempt the update (throttling handled inside)
      updateHuePosition(e);
    } else if (dragTypeRef.current === 'saturation') {
      // Update cursor position even if color update is throttled
      const rect = satCanvasRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
      const y = Math.max(0, Math.min(rect.height, e.clientY - rect.top));
      cursorPositionRef.current.satX = x;
      cursorPositionRef.current.satY = y;

      // Still attempt the update (throttling handled inside)
      updateSaturationPosition(e);
    }
  };

    // Handle touch move during drag
    const handleTouchMove = (e) => {
        e.preventDefault();

        if (!dragTypeRef.current) return;

        if (dragTypeRef.current === 'hue') {
            updateHuePosition(e);
        } else if (dragTypeRef.current === 'saturation') {
            updateSaturationPosition(e);
        }
    };

    // End drag operation
    const endDrag = () => {
        // Reset animation flag
        isAnimating.current = false;

        // Reset drag type
        dragTypeRef.current = null;

        // Important: Force one final update with the latest cursor position
        // to ensure the color matches where the cursor stopped
        if (cursorPositionRef.current) {
          isThrottled.current = false; // Bypass throttling for final update
          if (dragTypeRef.current === 'hue') {
            updateColorFromHSV(
              Math.round((cursorPositionRef.current.hue / HUE_CANVAS_WIDTH) * 360),
              saturation,
              brightness
            );
          } else if (dragTypeRef.current === 'saturation') {
            updateColorFromHSV(
              currentHueRef.current,
              cursorPositionRef.current.satX / SAT_CANVAS_WIDTH,
              1 - (cursorPositionRef.current.satY / SAT_CANVAS_HEIGHT)
            );
          }
        }


        // Remove event listeners
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', endDrag);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', endDrag);
    };

    // Handle clicks outside to close picker
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Clean up event listeners on unmount
    useEffect(() => {
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', endDrag);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', endDrag);
        };
    }, []);

  // Avoid ontouch epreventdefault error on mobile
  // Add this useEffect to handle touch event registration
  useEffect(() => {
    const hueCanvas = hueCanvasRef.current;
    const satCanvas = satCanvasRef.current;

    if (hueCanvas) {
      hueCanvas.addEventListener('touchstart', startHueDrag, { passive: false });
    }

    if (satCanvas) {
      satCanvas.addEventListener('touchstart', startSaturationDrag, { passive: false });
    }

    return () => {
      if (hueCanvas) {
        hueCanvas.removeEventListener('touchstart', startHueDrag);
      }
      if (satCanvas) {
        satCanvas.removeEventListener('touchstart', startSaturationDrag);
      }
    };
  }, [isOpen]); // Re-run when picker opens/closes

    return (

        <div className="custom-color-picker--container" ref={pickerRef}>
            <button
                style={{ background: validHex ? currentColor : 'linear-gradient(45deg, #f2f2f2 0%, #f2f2f2 40%, #9c9c9c 100%)' }}
                onClick={() => setIsOpen(!isOpen)}
                className="circle"
            >
                <svg style={{
                    fill: getContrastRatio(currentColor, '#ffffff') > 2.0
                        ? '#ffffff'
                        : '#161616'
                }} className="edit-icon" width={16} height={16} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="">
                    <path fillRule="evenodd" d="M15 4a3.001 3.001 0 0 1-2.25 2.905V8.5a.75.75 0 0 1-.22.53l-.5.5a.75.75 0 0 1-1.06 0l-.72-.72-4.677 4.678A1.75 1.75 0 0 1 4.336 14h-.672a.25.25 0 0 0-.177.073l-.707.707a.75.75 0 0 1-1.06 0l-.5-.5a.75.75 0 0 1 0-1.06l.707-.707A.25.25 0 0 0 2 12.336v-.672c0-.464.184-.909.513-1.237L7.189 5.75l-.72-.72a.75.75 0 0 1 0-1.06l.5-.5a.75.75 0 0 1 .531-.22h1.595A3.001 3.001 0 0 1 15 4ZM9.19 7.75l-.94-.94-4.677 4.678a.25.25 0 0 0-.073.176v.672c0 .058-.003.115-.009.173a1.74 1.74 0 0 1 .173-.009h.672a.25.25 0 0 0 .177-.073L9.189 7.75Z" clipRule="evenodd" />
                </svg>
            </button>

            {isOpen && (
                <div className="custom-color-picker--panel">
                    <div className="custom-color-picker--sat-container">
                        <canvas
                            ref={satCanvasRef}
                            className="custom-color-picker--sat-canvas"
                            onMouseDown={startSaturationDrag}
                        />
                    </div>

                    <div className="custom-color-picker--controls">
                        <div className="custom-color-picker--hue-container">
                            <canvas
                                ref={hueCanvasRef}
                                className="custom-color-picker--hue-canvas"
                                onMouseDown={startHueDrag}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomColorPicker;